import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useHorizontalScroll } from '../../modules/store/hooks/use-scroll';
import { enableScroll, preventScroll } from '../../utils/helpers/scroll';
import { IconChevron } from '../../assets/icons';
import './index.scss';

export type TabType = {
  label: string;
  name?: string;
  icon?: ReactNode;
  indicator?: boolean;
  addNew?: boolean;
  data?: number | string;
  error?: boolean;
  onClick?: () => void;
};

type TabsProps = {
  tabList: TabType[];
  activeTabIndex: number;
  setActiveTabIndex?: (activeTabIndex: number) => void;
  tabsType?: 'default' | 'pills' | 'compact';
  tabsBgColor?: 'grey' | 'white' | 'transparent';
  isDivider?: boolean;
  autoScrollDisabled?: boolean;
  tabsListClassName?: string;
  biggerTabs?: boolean;
  fullWidth?: boolean;
  moreButton?: 'normal' | 'secondary';
  moreListFullHeight?: boolean;
  children?: React.ReactNode;
};

export const Tabs: React.FC<TabsProps> = ({
  tabList,
  activeTabIndex,
  setActiveTabIndex,
  tabsType = 'default',
  tabsBgColor = 'white',
  isDivider = true,
  autoScrollDisabled = false,
  tabsListClassName,
  children,
  biggerTabs,
  fullWidth = true,
  moreButton,
  moreListFullHeight,
}) => {
  const [moreOpen, setMoreOpen] = useState<boolean>(false);
  const { scrollableRef } = useHorizontalScroll<HTMLUListElement>();

  const setBgGradient = useCallback(() => {
    if (scrollableRef && scrollableRef.current) {
      const scrollWidth = scrollableRef.current.scrollWidth;
      const scrollLeft = scrollableRef.current.scrollLeft;

      scrollableRef?.current?.parentElement?.classList.toggle('tabs--gradient-left', scrollLeft > 20);
      scrollableRef?.current?.parentElement?.classList.toggle(
        'tabs--gradient-right',
        scrollableRef.current.offsetWidth + scrollLeft < scrollWidth,
      );
    }
  }, [scrollableRef]);

  useEffect(() => {
    window.addEventListener('resize', setBgGradient);

    return () => window.removeEventListener('resize', setBgGradient);
  }, []);

  useEffect(() => {
    if (scrollableRef && scrollableRef.current && !autoScrollDisabled) {
      const activeElement = scrollableRef?.current.children[activeTabIndex];
      if (activeElement) {
        activeElement.scrollIntoView({ block: 'nearest', inline: 'center' });
        setBgGradient();
      }
    }
  }, [scrollableRef?.current?.scrollWidth, activeTabIndex]);

  useEffect(() => {
    if (scrollableRef && scrollableRef.current) {
      scrollableRef.current.addEventListener('scroll', setBgGradient);

      return () => scrollableRef.current?.removeEventListener('scroll', setBgGradient);
    }
  }, [scrollableRef]);

  const toggleMore = () => {
    setMoreOpen(!moreOpen);

    if (!moreOpen) preventScroll();
    else enableScroll();
  };

  const handleChangeTab = (tabIndex: number, tab: TabType) => {
    enableScroll();
    setMoreOpen(false);

    if (tab.onClick) tab.onClick();
    else if (setActiveTabIndex) {
      setActiveTabIndex(tabIndex);
    }
  };

  return (
    <>
      {tabList.length > 0 && (
        <div
          className={classNames(`tabs tabs--type-${tabsType} tabs--${tabsBgColor}`, {
            'bigger-tabs': biggerTabs,
            'more-btn': moreButton,
          })}
        >
          {tabList.length > 1 && moreButton && (
            <button onClick={toggleMore} className={classNames('tabs__more-btn', { open: moreOpen })}>
              <IconChevron />
            </button>
          )}
          <ul
            ref={scrollableRef}
            className={classNames('tabs__list', {
              'full-width': fullWidth && !moreButton,
              'no-padding': moreButton,
              [tabsListClassName as string]: tabsListClassName,
            })}
            {...(moreOpen ? { style: { visibility: 'hidden' } } : {})}
          >
            {tabList.map((tab: TabType, tabIndex: number) => (
              <li key={tabIndex} onClick={() => handleChangeTab(tabIndex, tab)} className="tabs__list-item-wrapper">
                <div
                  className={classNames('tabs__list-item', {
                    'tabs__list-item--active': tabIndex === activeTabIndex,
                    'tabs__list-item--bigger': biggerTabs,
                    'tabs__list-item--indicator': tab.indicator,
                    'tabs__list-item--add-new': tab.addNew,
                    'tabs__list-item--error': tab.error,
                  })}
                >
                  <div className="tabs__list-item-content">
                    {tab.icon}
                    <span className="text-ellipsis">{tab.label}</span>
                    {tab.indicator && <div className="indicator" />}
                  </div>
                </div>
              </li>
            ))}
            {children}
          </ul>
          {moreOpen && (
            <div
              className={classNames('more-tabs-list', {
                secondary: moreButton === 'secondary',
                'full-height': moreListFullHeight,
              })}
            >
              <ul className="grid-list-24">
                {tabList.map((tab: TabType, tabIndex: number) => (
                  <li
                    key={tabIndex}
                    onClick={() => handleChangeTab(tabIndex, tab)}
                    className={classNames({ active: tabIndex === activeTabIndex, 'add-new': tab.addNew })}
                  >
                    <div className="item-content">
                      {tab.icon && <div className="icon">{tab.icon}</div>}
                      <span className="text-ellipsis">{tab.label}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {isDivider && !moreOpen && <hr className="tabs__line" />}
    </>
  );
};
